import heic2any from 'heic2any'
import * as UTIF from 'utif'
import { isHeicBlob as isHeicLike, isTiffBlob as isTiffLike, toJpegFileName } from './imageFormat'

export { isHeicBlob, isTiffBlob, toJpegFileName } from './imageFormat'

async function readViaImageElement(blob: Blob): Promise<{ width: number; height: number }> {
    const img = await loadHtmlImage(blob)
    return { width: img.naturalWidth || 0, height: img.naturalHeight || 0 }
}

async function decodeHeicToImageBlob(blob: Blob): Promise<Blob> {
    const out = await heic2any({
        blob,
        toType: 'image/jpeg',
        quality: 0.92,
    } as any)

    const first = Array.isArray(out) ? out[0] : out
    if (!(first instanceof Blob)) throw new Error('HEIC conversion failed')
    return first
}

async function loadHtmlImage(blob: Blob): Promise<HTMLImageElement> {
    const url = URL.createObjectURL(blob)
    try {
        return await new Promise<HTMLImageElement>((resolve, reject) => {
            const el = new Image()
            el.onload = () => resolve(el)
            el.onerror = () => reject(new Error('image decode failed'))
            el.src = url
        })
    } finally {
        URL.revokeObjectURL(url)
    }
}

async function loadBitmapWithOrientation(blob: Blob): Promise<ImageBitmap> {
    try {
        return await createImageBitmap(blob, { imageOrientation: 'from-image' } as any)
    } catch {
        return await createImageBitmap(blob)
    }
}

async function blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
    return await blob.arrayBuffer()
}

type RenderableSource = {
    width: number
    height: number
    draw: (ctx: any, dx: number, dy: number, dw: number, dh: number) => void
    close?: () => void
}

// --- Off-main-thread TIFF decode (with graceful main-thread fallback) ----------
let decodeWorker: Worker | null = null
let decodeWorkerBroken = false
let decodeReqId = 0
const decodePending = new Map<number, { resolve: (v: any) => void; reject: (e: any) => void }>()

function failAllPendingDecodes(err: Error) {
    for (const [, p] of decodePending) p.reject(err)
    decodePending.clear()
}

function getDecodeWorker(): Worker | null {
    if (decodeWorkerBroken) return null
    if (typeof Worker === 'undefined' || typeof createImageBitmap === 'undefined') return null
    if (decodeWorker) return decodeWorker
    try {
        decodeWorker = new Worker(new URL('./imageDecode.worker.ts', import.meta.url), { type: 'module' })
        decodeWorker.onmessage = (e: MessageEvent) => {
            const { id, ok, bitmap, width, height, error } = (e.data || {}) as any
            const p = decodePending.get(id)
            if (!p) return
            decodePending.delete(id)
            if (ok) p.resolve({ bitmap, width, height })
            else p.reject(new Error(error || 'decode failed'))
        }
        decodeWorker.onerror = () => {
            // Worker is unusable in this environment — disable it and fall back.
            decodeWorkerBroken = true
            failAllPendingDecodes(new Error('decode worker error'))
            try { decodeWorker?.terminate() } catch {}
            decodeWorker = null
        }
        return decodeWorker
    } catch {
        decodeWorkerBroken = true
        return null
    }
}

async function decodeTiffViaWorker(blob: Blob): Promise<RenderableSource | null> {
    const worker = getDecodeWorker()
    if (!worker) return null
    const buffer = await blobToArrayBuffer(blob)
    const id = ++decodeReqId
    const result = await new Promise<{ bitmap: ImageBitmap; width: number; height: number }>((resolve, reject) => {
        decodePending.set(id, { resolve, reject })
        try {
            worker.postMessage({ id, kind: 'tiff', buffer }, [buffer])
        } catch (err) {
            decodePending.delete(id)
            reject(err)
        }
    })
    const bmp = result.bitmap
    return {
        width: result.width,
        height: result.height,
        draw: (ctx, dx, dy, dw, dh) => ctx.drawImage(bmp, dx, dy, dw, dh),
        close: () => bmp.close?.(),
    }
}

async function decodeTiffRenderableSource(blob: Blob): Promise<RenderableSource> {
    const buffer = await blobToArrayBuffer(blob)
    const ifds = UTIF.decode(buffer)
    if (!Array.isArray(ifds) || ifds.length === 0) {
        throw new Error('TIFF decode failed')
    }

    const ifd = ifds[0] as any
    UTIF.decodeImage(buffer, ifd)

    const width = Math.max(1, Number(ifd?.width || ifd?.t256?.[0] || 0))
    const height = Math.max(1, Number(ifd?.height || ifd?.t257?.[0] || 0))
    if (width <= 0 || height <= 0) {
        throw new Error('TIFF size missing')
    }

    const rgba = UTIF.toRGBA8(ifd)
    const { canvas, ctx } = createCanvas(width, height)
    const imageData = new ImageData(new Uint8ClampedArray(rgba), width, height)
    ctx.putImageData(imageData, 0, 0)

    return {
        width,
        height,
        draw: (targetCtx, dx, dy, dw, dh) => {
            targetCtx.drawImage(canvas as any, dx, dy, dw, dh)
        },
    }
}

async function loadRenderableSource(blob: Blob): Promise<RenderableSource> {
    if (isTiffLike(blob)) {
        // Prefer the worker (keeps the full-res TIFF decode off the main thread);
        // fall back to the synchronous decode on any worker failure.
        try {
            const viaWorker = await decodeTiffViaWorker(blob)
            if (viaWorker) return viaWorker
        } catch {
            /* fall through to main-thread decode */
        }
        return await decodeTiffRenderableSource(blob)
    }

    try {
        const bmp = await loadBitmapWithOrientation(blob)
        return {
            width: bmp.width,
            height: bmp.height,
            draw: (ctx, dx, dy, dw, dh) => {
                ctx.drawImage(bmp, dx, dy, dw, dh)
            },
            close: () => bmp.close?.(),
        }
    } catch {
        const img = await loadHtmlImage(blob)
        const width = img.naturalWidth || img.width || 0
        const height = img.naturalHeight || img.height || 0
        return {
            width,
            height,
            draw: (ctx, dx, dy, dw, dh) => {
                ctx.drawImage(img, dx, dy, dw, dh)
            },
        }
    }
}

function createCanvas(width: number, height: number): { canvas: OffscreenCanvas | HTMLCanvasElement; ctx: any } {
    let canvas: OffscreenCanvas | HTMLCanvasElement
    let ctx: any

    if (typeof OffscreenCanvas !== 'undefined') {
        canvas = new OffscreenCanvas(width, height)
        ctx = canvas.getContext('2d')
    } else {
        canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        ctx = canvas.getContext('2d')
    }

    if (!ctx) throw new Error('Could not create 2D canvas context')
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    return { canvas, ctx }
}

async function canvasToBlob(canvas: OffscreenCanvas | HTMLCanvasElement, mime: string, quality: number): Promise<Blob> {
    return canvas instanceof OffscreenCanvas
        ? await canvas.convertToBlob({ type: mime, quality })
        : await new Promise((resolve, reject) =>
            (canvas as HTMLCanvasElement).toBlob(b => (b ? resolve(b) : reject(new Error('toBlob failed'))), mime, quality),
        )
}

export async function normalizeImageToJpeg(
    blob: Blob,
    opts: { quality?: number; background?: string } = {},
): Promise<{ blob: Blob; width: number; height: number; mime: 'image/jpeg' }> {
    const quality = Number.isFinite(opts.quality) ? Number(opts.quality) : 0.92
    const background = String(opts.background || '#ffffff')
    let sourceBlob = blob

    if (isHeicLike(blob)) {
        try {
            const bmp = await loadBitmapWithOrientation(blob)
            bmp.close?.()
        } catch {
            try {
                await loadHtmlImage(blob)
            } catch {
                sourceBlob = await decodeHeicToImageBlob(blob)
            }
        }
    }

    try {
        const source = await loadRenderableSource(sourceBlob)
        try {
            const width = source.width
            const height = source.height
            const { canvas, ctx } = createCanvas(width, height)
            ctx.fillStyle = background
            ctx.fillRect(0, 0, width, height)
            source.draw(ctx, 0, 0, width, height)
            const out = await canvasToBlob(canvas, 'image/jpeg', quality)
            return { blob: out, width, height, mime: 'image/jpeg' }
        } finally {
            source.close?.()
        }
    } catch (bitmapErr: any) {
        try {
            const img = await loadHtmlImage(sourceBlob)
            const width = img.naturalWidth || img.width || 0
            const height = img.naturalHeight || img.height || 0
            const { canvas, ctx } = createCanvas(width, height)
            ctx.fillStyle = background
            ctx.fillRect(0, 0, width, height)
            ctx.drawImage(img, 0, 0, width, height)
            const out = await canvasToBlob(canvas, 'image/jpeg', quality)
            return { blob: out, width, height, mime: 'image/jpeg' }
        } catch {
            if (isHeicLike(blob)) {
                throw new Error('HEIC konnte in diesem Browser nicht gelesen werden. Bitte JPG/PNG verwenden oder die Handy-Upload-Seite in Safari öffnen.')
            }
            throw bitmapErr
        }
    }
}

export async function normalizeImageForUpload(
    blob: Blob,
    fileName = 'bild',
    opts: { quality?: number; background?: string } = {},
): Promise<{ blob: Blob; width: number; height: number; mime: string; name: string; normalized: boolean }> {
    try {
        const normalized = await normalizeImageToJpeg(blob, opts)
        return {
            blob: normalized.blob,
            width: normalized.width,
            height: normalized.height,
            mime: normalized.mime,
            name: toJpegFileName(fileName),
            normalized: true,
        }
    } catch (err) {
        if (isHeicLike(blob) || isTiffLike(blob)) {
            return {
                blob,
                width: 0,
                height: 0,
                mime: String(blob.type || (isTiffLike(blob) ? 'image/tiff' : 'image/heic')),
                name: String(fileName || (isTiffLike(blob) ? 'bild.tiff' : 'bild.heic')),
                normalized: false,
            }
        }
        throw err
    }
}

export async function readImageDimensions(blob: Blob): Promise<{ width: number; height: number }> {
    try {
        const source = await loadRenderableSource(blob)
        source.close?.()
        return { width: source.width, height: source.height }
    } catch {
        try {
            return await readViaImageElement(blob)
        } catch {
            return { width: 0, height: 0 }
        }
    }
}
export async function createThumb(
    blob: Blob,
    maxSidePx = 560,
    mime = 'image/jpeg',
    quality = 0.86,
): Promise<{ blob: Blob; width: number; height: number }> {
    const source = await loadRenderableSource(blob)
    try {
        const w = source.width
        const h = source.height

        const scale = Math.min(1, maxSidePx / Math.max(w, h))
        const dstW = Math.max(1, Math.round(w * scale))
        const dstH = Math.max(1, Math.round(h * scale))
        const { canvas, ctx } = createCanvas(dstW, dstH)
        source.draw(ctx, 0, 0, dstW, dstH)
        const out = await canvasToBlob(canvas, mime, quality)

        return { blob: out, width: dstW, height: dstH }
    } finally {
        source.close?.()
    }
}

export async function createEditorPreview(
    blob: Blob,
    maxSidePx = 1600,
    mime = 'image/webp',
    quality = 0.82,
): Promise<{ blob: Blob; width: number; height: number }> {
    return createThumb(blob, maxSidePx, mime, quality)
}



export async function downscaleToFit(
    blob: Blob,
    maxWidthPx: number,
    maxHeightPx: number,
    mime = 'image/jpeg',
    quality = 0.92,
): Promise<{ blob: Blob; width: number; height: number; scaled: boolean }> {
    const source = await loadRenderableSource(blob)
    try {
        const srcW = source.width
        const srcH = source.height

        const scale = Math.min(1, maxWidthPx / srcW, maxHeightPx / srcH)
        const dstW = Math.max(1, Math.round(srcW * scale))
        const dstH = Math.max(1, Math.round(srcH * scale))

        if (scale >= 1) {
            return { blob, width: srcW, height: srcH, scaled: false }
        }

        const { canvas, ctx } = createCanvas(dstW, dstH)
        source.draw(ctx, 0, 0, dstW, dstH)
        const outBlob = await canvasToBlob(canvas, mime, quality)

        return { blob: outBlob, width: dstW, height: dstH, scaled: true }
    } finally {
        source.close?.()
    }
}
